import { formatCurrency } from '@quno/patient-journey/src/utils/currency';
import { CURRENCY } from '@quno/patient-journey/src/constants/payment';
import { useTranslation } from '@quno/patient-journey/src/hooks/useTranslation';
import { Card } from '@quno/patient-journey/src/components/Card/Card';
import styles from '@quno/patient-journey/src/components/Card/Variants/TreatmentCard.module.scss';
import { useEffect, useState } from 'react';
import { localeMapper } from '@quno/onboarding/src/constants/locales';
import { defaultLocale } from '@quno/patient-journey/src/constants/locales';
import { fetchContentfulGql } from '@quno/onboarding/src/services/contentful';
import { renderImage } from '@quno/patient-journey/src/contentful/patient-journey/renderImage';
import type { Packages } from '@quno/patient-journey/src/contentful/generated/graphql';
import type { CardProps } from '@quno/patient-journey/src/components/Card/card.config';
import type { FormLocales } from '@quno/onboarding/src/constants/locales';

export type TreatmentCardProps = {
  footer?: string | null;
  cta?: JSX.Element | null;
  productId?: string | null;
  locale: FormLocales;
  imageBorderRadius?: string;
  hidePrice?: boolean | null;
};

export const TreatmentCard = ({
  footer,
  cta,
  fontColorOverride,
  backgroundColorOverwrite,
  cardBorderRadius,
  imageBorderRadius,
  productId,
  hidePrice,
  ...props
}: CardProps & TreatmentCardProps): JSX.Element => {
  const { locale: pageLocale, t } = useTranslation(true);
  const locale = props.locale || localeMapper[pageLocale] || defaultLocale;
  const [contentfulProduct, setContentfulProduct] =
    useState<
      Pick<
        Packages,
        | 'duration'
        | 'description'
        | 'image'
        | 'name'
        | 'price'
        | 'startingFromPrice'
      >
    >();

  useEffect(() => {
    void (async () => {
      const contentfulPackage = await fetchContentfulGql({
        query: `
          query {
            packages: packages(id: "${productId}", locale: "${locale}") {
              name
              description
              duration
              price
              startingFromPrice
              image {
                __typename
                sys {
                  id
                }
                url
                width
                height
              }
            }
          }
        `,
        variables: {},
      });
      setContentfulProduct(contentfulPackage?.data?.packages ?? {});
    })();
  }, [locale, productId]);

  const packageImage = contentfulProduct?.image?.url
    ? renderImage(
        { ...contentfulProduct?.image },
        {
          container: true,
          fill: true,
          style: { borderRadius: imageBorderRadius || 0 },
        },
      )
    : null;

  const packagePrice = contentfulProduct?.price;

  return (
    <Card
      className={styles.item}
      footer={footer}
      fontColorOverride={fontColorOverride}
      backgroundColorOverwrite={backgroundColorOverwrite}
      style={{ borderRadius: cardBorderRadius || 0 }}
    >
      <div className={styles.imageWrapper}>{packageImage}</div>
      <div>
        <h4
          className={styles.title}
          style={{
            color: fontColorOverride,
          }}
        >
          {contentfulProduct?.name}
        </h4>
        {contentfulProduct?.duration && (
          <div className={styles.duration}>
            <span>{`${contentfulProduct?.duration} min`}</span>
          </div>
        )}
      </div>
      <div className={styles.description}>{contentfulProduct?.description}</div>
      <div className={styles.footer}>
        <p className={styles.price}>
          {!!packagePrice && !hidePrice && (
            <>
              {contentfulProduct?.startingFromPrice && (
                <>
                  <span>{t('from')}</span>{' '}
                </>
              )}
              <span>{formatCurrency(packagePrice || 0, CURRENCY, locale)}</span>
            </>
          )}
        </p>
        <div>{cta}</div>
      </div>
    </Card>
  );
};
